// Lib
import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Skeleton } from "antd";
// Api
import { useGetCustomerMenuByLocationQuery } from "rtkQuery/query/customersAPI";
import {
  useGetProductsMediaByIdQuery,
  useUpdateLazyProductMediaMutation,
} from "rtkQuery/query/mediaAPI";
// Hooks
import { useNotification, useViewport } from "hooks";
// Actions
// Selectors
// Types
import { MediaItem } from "./types";
// Theme
import { theme } from "theme";
// Constants
import { ADMIN_ROUTES, rtkQueryParams } from "consts";
// Helpers
import { generateUniqueId } from "helpers/dataHelpers";
// Utils
import { errorHandler } from "utils/errorHandler";
// Icons
import { DescriptionBlackIcon, PlusIcon, RightBurgerMenuIcon } from "icons";
// Layouts
// Components
import { ArrowBackLink, DropDown, NoItemsContent } from "components";
import { ConfirmDialog } from "components/Modals";
import { ImageUploadModal, MediaContent, VideoUploadModal } from "./components";
// Styled
import {
  ContentBox,
  FlexContainer,
  PageHeadingContainer,
  PageTitleContainer,
} from "styled/Box";
import { Typography } from "styled/Typography";
import { Button } from "styled/Buttons";

export const ProductMediaDetails: FC = () => {
  const { openNotification } = useNotification();
  const { isMobile } = useViewport();
  const navigate = useNavigate();

  const { providerId, locationId } = useParams();

  if (!providerId || !locationId) {
    openNotification({
      type: "error",
      message: "Invalid parameters provided.",
    });
    navigate(`${ADMIN_ROUTES.PRODUCT_MEDIA.path}`);
  }

  const [imageUploadModal, setImageUploadModal] = useState(false);
  const [videoUploadModal, setVideoUploadModal] = useState(false);
  const [errorDialog, setErrorDialog] = useState<string | null>(null);

  const [items, setItems] = useState<MediaItem[]>([]);

  const {
    data: menu,
    isFetching: isMenuFetching,
    error: menuError,
  } = useGetCustomerMenuByLocationQuery(
    {
      id: locationId || "",
    },
    { skip: !locationId || !providerId },
  );

  const { data: media, isFetching: isMediaFetching } =
    useGetProductsMediaByIdQuery(
      {
        providerId: providerId || "",
      },
      { ...rtkQueryParams, skip: !providerId || !locationId },
    );

  useEffect(() => {
    const items =
      media?.media?.map(item => ({
        id: generateUniqueId(),
        imageURL: item.imageURL,
        posterURL: item.posterURL,
        order: item.order,
      })) || [];

    setItems(items);
  }, [media]);

  useEffect(() => {
    if (menuError) {
      errorHandler({ error: menuError, openNotification });
    }
  }, [menuError]);

  const [update, { isLoading, error }] = useUpdateLazyProductMediaMutation();

  useEffect(() => {
    if (menuError) {
      errorHandler({ error: menuError, openNotification });
    }
  }, [menuError]);

  useEffect(() => {
    if (error) {
      errorHandler({ error, openNotification });
    }
  }, [error]);

  const videos = media?.media?.filter(item => !!item?.posterURL) || [];
  const images = media?.media?.filter(item => !!item?.imageURL) || [];

  const addMediaItems = useMemo(() => {
    return [
      {
        label: "Add Image",
        key: "add-image",
        onClick: () =>
          images?.length > 9
            ? setErrorDialog("Images media limit has been reached")
            : setImageUploadModal(true),
      },
      {
        label: "Add Video",
        key: "add-video",
        onClick: () =>
          videos?.length > 0
            ? setErrorDialog("Videos media limit has been reached")
            : setVideoUploadModal(true),
      },
    ];
  }, [images]);

  const handleUpload = async (data: {
    imageURL?: string;
    posterURL?: string;
    videoURL?: string;
  }) => {
    if (!providerId || !locationId) {
      openNotification({
        type: "error",
        message: "Invalid parameters provided.",
      });

      return;
    }

    try {
      const orderedItems = items.map((item, index) => {
        const { imageURL, posterURL, videoURL } = item || {};

        return {
          imageURL,
          posterURL,
          videoURL,
          order: index,
        };
      });

      await update({
        providerId,
        media: [
          ...(orderedItems || []),
          {
            ...data,
            order: orderedItems?.length || 0,
          },
        ],
      }).unwrap();

      setItems(prev => [
        ...prev,
        { ...data, order: prev.length || 0, id: generateUniqueId() },
      ]);

      openNotification({ message: "Media uploaded successfully." });

      setImageUploadModal(false);
      setVideoUploadModal(false);
    } catch (error) {
      errorHandler({ error, openNotification });
      return;
    }
  };

  const productData = useMemo(
    () =>
      menu?.categories
        ?.flatMap(category => category.products)
        .find(product => product.providerId === providerId),
    [menu, providerId],
  );

  const noContent =
    (!isMediaFetching && !media) || (!isMediaFetching && !media?.media?.length);

  return (
    <>
      <FlexContainer $fullwidth $column $gap={24}>
        <PageHeadingContainer>
          <PageTitleContainer $column $gap={16}>
            <ArrowBackLink
              title="Back to Product Media"
              path={`${ADMIN_ROUTES.PRODUCT_MEDIA.path}?locationId=${locationId}`}
            />

            <Typography.H1>Product Media Details</Typography.H1>
          </PageTitleContainer>
        </PageHeadingContainer>

        <ContentBox $column $gap={20}>
          <FlexContainer $justify="space-between" $align="center">
            {isMenuFetching ? (
              <Skeleton.Input active />
            ) : (
              <Typography.H2>{productData?.name}</Typography.H2>
            )}

            <DropDown trigger={["click"]} items={addMediaItems}>
              {!isMobile ? (
                <Button.Heading
                  icon={<PlusIcon fill={theme.color.white} />}
                  type="primary"
                >
                  Add Media
                </Button.Heading>
              ) : (
                <Button.SquaredIcon icon={<RightBurgerMenuIcon />} />
              )}
            </DropDown>
          </FlexContainer>

          <MediaContent
            isMediaFetching={isMediaFetching}
            items={items}
            setItems={setItems}
          />
        </ContentBox>
      </FlexContainer>

      <ImageUploadModal
        isLoading={isLoading}
        open={imageUploadModal}
        handleClose={() => setImageUploadModal(false)}
        handleUpload={handleUpload}
      />

      <VideoUploadModal
        isLoading={isLoading}
        open={videoUploadModal}
        handleClose={() => setVideoUploadModal(false)}
        handleUpload={handleUpload}
      />

      <ConfirmDialog
        open={!!errorDialog}
        message={errorDialog}
        description="To upload media, please select another media type or delete the existing one."
        onCancel={() => setErrorDialog(null)}
        isLoading={false}
        Icon={DescriptionBlackIcon}
      />
    </>
  );
};
